import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { STUDENT_NAV } from "@/pages/student/StudentHome";
import { MessageThread } from "@/components/dashboard/MessageThread";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, MapPin, Briefcase, CheckCircle2, MessageSquare, Globe } from "lucide-react";

function MatchBadge({ v }) {
  const c = v >= 80 ? "#10b981" : v >= 60 ? "#78be21" : "#d97706";
  return <span className="rounded-full px-2.5 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: c }}>{v}% match</span>;
}

export default function StudentJobs() {
  const [tab, setTab] = useState("browse");
  const [jobs, setJobs] = useState(null);
  const [apps, setApps] = useState(null);
  const [detail, setDetail] = useState(null);
  const [msgApp, setMsgApp] = useState(null);

  const loadJobs = () => api.get("/jobs").then(({ data }) => setJobs(data));
  const loadApps = () => api.get("/applications/mine").then(({ data }) => setApps(data));
  useEffect(() => { loadJobs(); loadApps(); }, []);

  const apply = async (job) => {
    try { await api.post(`/jobs/${job.id}/apply`); toast.success("Application sent!"); loadJobs(); loadApps(); setDetail(null); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <DashboardShell title="Jobs & Internships" subtitle="Postings matched to your transferable skills" nav={STUDENT_NAV}>
      <div className="mb-5 flex gap-2">
        <Button data-testid="jobs-tab-browse" onClick={() => setTab("browse")} variant={tab === "browse" ? "default" : "outline"} className="rounded-full">Browse jobs</Button>
        <Button data-testid="jobs-tab-apps" onClick={() => setTab("apps")} variant={tab === "apps" ? "default" : "outline"} className="rounded-full">My applications</Button>
      </div>

      {tab === "browse" && (
        <>
          {!jobs && <div className="flex h-48 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#003057]" /></div>}
          {jobs && jobs.length === 0 && <Card className="p-8 text-center text-slate-500">No open postings yet. Check back soon.</Card>}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {jobs && jobs.map((j) => (
              <Card key={j.id} className="flex flex-col p-5 card-elevate" data-testid="student-job-card">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3">
                    {j.company_logo && <img src={j.company_logo} alt={j.company} className="h-11 w-11 flex-shrink-0 rounded-lg border border-slate-200 object-contain bg-white p-1" />}
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-widest text-[#5fa114]">{j.job_type} · {j.company}{j.industry ? ` · ${j.industry}` : ""}</span>
                      <h3 className="mt-0.5 font-heading text-lg font-bold text-[#003057]">{j.title}</h3>
                    </div>
                  </div>
                  <MatchBadge v={j.match ?? 0} />
                </div>
                <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-500"><MapPin className="h-4 w-4" /> {j.location || "—"} · {j.remote_mode}</p>
                <p className="mt-2 line-clamp-2 text-sm text-slate-600">{j.description}</p>
                <div className="mt-auto flex gap-2 pt-4">
                  <Button size="sm" variant="outline" className="rounded-full" data-testid="job-view" onClick={() => setDetail(j)}>View</Button>
                  {j.applied
                    ? <Button size="sm" disabled className="rounded-full"><CheckCircle2 className="mr-1 h-4 w-4" /> Applied</Button>
                    : <Button size="sm" className="rounded-full bg-[#78be21] font-bold text-[#00243f] hover:bg-[#6aac1c]" data-testid="job-apply" onClick={() => apply(j)}>One-click apply</Button>}
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {tab === "apps" && (
        <>
          {!apps && <div className="flex h-48 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#003057]" /></div>}
          {apps && apps.length === 0 && <Card className="p-8 text-center text-slate-500">You haven't applied to any postings yet.</Card>}
          <div className="space-y-3">
            {apps && apps.map((a) => (
              <Card key={a.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid="student-application">
                <div>
                  <p className="font-semibold text-[#003057]">{a.job_title} <span className="font-normal text-slate-400">· {a.company}</span></p>
                  <p className="text-xs text-slate-500">Applied {a.created_at?.slice(0, 10)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <MatchBadge v={a.match ?? 0} />
                  <Button size="sm" variant="outline" className="rounded-full" data-testid="app-message" onClick={() => setMsgApp(a)}><MessageSquare className="mr-1 h-4 w-4" /> Message</Button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Job detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          {detail && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  {detail.company_logo && <img src={detail.company_logo} alt={detail.company} className="h-12 w-12 rounded-lg border border-slate-200 object-contain bg-white p-1" />}
                  <div>
                    <span className="text-xs font-bold uppercase tracking-widest text-[#5fa114]">{detail.job_type} · {detail.company}{detail.industry ? ` · ${detail.industry}` : ""}</span>
                    <DialogTitle className="font-heading text-2xl text-[#003057]">{detail.title}</DialogTitle>
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-3 text-sm text-slate-700">
                <p className="flex items-center gap-1.5 text-slate-500"><MapPin className="h-4 w-4" /> {detail.location || "—"} · {detail.remote_mode}</p>
                {detail.company_website && <p><a href={detail.company_website.startsWith("http") ? detail.company_website : `https://${detail.company_website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-semibold text-[#005291] hover:text-[#78be21]" data-testid="job-website-link"><Globe className="h-4 w-4" /> Company website</a></p>}
                {(detail.pay_min || detail.pay_max) && <p><strong>Pay:</strong> {detail.pay_min}–{detail.pay_max} / {detail.pay_period}</p>}
                {detail.description && <div><p className="font-bold text-[#003057]">About</p><p>{detail.description}</p></div>}
                {detail.responsibilities && <div><p className="font-bold text-[#003057]">Responsibilities</p><p className="whitespace-pre-line">{detail.responsibilities}</p></div>}
                {detail.qualifications && <div><p className="font-bold text-[#003057]">Qualifications</p><p className="whitespace-pre-line">{detail.qualifications}</p></div>}
                {detail.apply_by && <p className="text-slate-500">Apply by {detail.apply_by}</p>}
              </div>
              {!detail.applied && <Button className="mt-4 w-full rounded-full bg-[#78be21] font-bold text-[#00243f] hover:bg-[#6aac1c]" onClick={() => apply(detail)}>One-click apply with my GPA+ profile</Button>}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Messaging */}
      <Dialog open={!!msgApp} onOpenChange={(o) => !o && setMsgApp(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-lg text-[#003057]">{msgApp?.job_title} · {msgApp?.company}</DialogTitle></DialogHeader>
          {msgApp && <MessageThread applicationId={msgApp.id} />}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
